import React, { FC, useEffect, useCallback, useState } from "react";
import styled from "@emotion/styled";
import { Checkbox, Button } from "antd";
import { ExclamationCircleFilled } from "src/icons";
import anomalyIndicatorService from "src/shared/services/anomalyIndicator.service";
import { useRedux } from "src/shared/hooks";
import { setShowSnackbar } from "src/store/app";
import { IRootState } from "src/shared/types";
import { IActionFactory } from "src/store";
import colors from "src/styles/variables.scss";
import { safeGet } from "src/shared/utils";

/**
 * Show snackbar with info about warning sign
 * the first 3 times app is opened
 * or till the user selects dont show again option
 */
const DontShowAgain = styled.section`
  grid-column: 1 / span 2;
  text-align: right;
  span {
    color: ${colors.snackbarText};
    opacity: 0.8;
    font-size: 0.96rem;
  }
`;

const SnackbarContainer = styled.section`
  left: unset;
  width: inherit;
  margin: 0;
  /* opacity: 0; */
  position: fixed;
  top: 30px;
  -webkit-user-select: none; /* Safari 3.1+ */
  -moz-user-select: none; /* Firefox 2+ */
  -ms-user-select: none; /* IE 10+ */
  user-select: none;
  -webkit-animation: fadein 0.5s;
  animation: fadein 0.5s;

  @-webkit-keyframes fadein {
    from {
      top: 0;
      opacity: 0;
    }
    to {
      top: 30px;
      opacity: 1;
    }
  }

  @keyframes fadein {
    from {
      top: 0;
      opacity: 0;
    }
    to {
      top: 30px;
      opacity: 1;
    }
  }
`;

const SnackBar = styled.section`
  background-color: white;
  text-align: left;
  border-radius: 3px;
  padding: 16px;
  z-index: 1;
  width: calc(100% - 40px);
  margin: 0 20px;
  font-size: 1.04rem;
  color: ${colors.snackbarText};
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 10px;
  > p > span {
    vertical-align: text-top;
    font-size: 14px;
    padding: 0 5px;
    color: ${colors.anomalyIndicator};
  }
  & > span {
    margin: auto 5px;
    color: #a1c2fa;
  }
  p {
    margin: 0px;
  }
`;

const SnackbarComponent: FC<{}> = () => {
  const [dontShowAgain, setDontShowAgain] = useState(anomalyIndicatorService.getVisitedCount() > 3);
  const {
    shouldShowSnackbar,
    setShowSnackbar: setShowSnackbarAction,
    isRoleCalloutShown,
  } = useRedux<
    IRootState,
    { shouldShowSnackbar: boolean; isRoleCalloutShown: boolean },
    { setShowSnackbar: IActionFactory<symbol, boolean> },
    {}
  >(
    (state) => ({
      shouldShowSnackbar: state.app.shouldShowSnackbar,
      isRoleCalloutShown: state.app.isRoleCalloutShown,
    }),
    { setShowSnackbar }
  );

  useEffect(() => {
    const shouldShow =
      !window.location.pathname.includes("login") &&
      !window.location.pathname.includes("oauth") &&
      anomalyIndicatorService.shouldShow;
    setTimeout(() => {
      shouldShow && setShowSnackbarAction(shouldShow);
    }, 2000);
    shouldShow && anomalyIndicatorService.incrementVisitedCount();
  }, []);

  const clickHandler = useCallback(() => {
    setShowSnackbarAction(false);
    anomalyIndicatorService.incrementVisitedCount();
    dontShowAgain && anomalyIndicatorService.setDisableForever();
  }, [anomalyIndicatorService, dontShowAgain]);

  const onSelectHandler = useCallback(
    (e) => {
      setDontShowAgain(safeGet(e, "target.checked", false));
    },
    [setDontShowAgain]
  );

  return shouldShowSnackbar && isRoleCalloutShown ? (
    <SnackbarContainer>
      <SnackBar>
        <p>
          Metric data with
          <ExclamationCircleFilled /> symbol might be temporarily inaccurate.
        </p>
        <Button type="primary" onClick={clickHandler}>
          Got it
        </Button>
        <DontShowAgain>
          <Checkbox onChange={onSelectHandler} checked={dontShowAgain}>
            Don't show again.
          </Checkbox>
        </DontShowAgain>
      </SnackBar>
    </SnackbarContainer>
  ) : null;
};

export default SnackbarComponent;
